// IE Hub - Customer ID Resolution Utility
// Resolves customer ID from multiple sources following the integration plan strategy

import { ApiServices } from '../api/services';

/**
 * Customer ID Resolution Strategy (as per integration plan):
 * 1. URL query param 'customerId'
 * 2. localStorage.customerData.customerId (or id)
 * 3. Derive via OnboardingService.getCustomerByUserId(userId) from localStorage.userData.userId and cache into localStorage
 */

/**
 * Resolve customer ID using the documented strategy
 * @param searchParams - URL search params (optional, will use window.location if not provided)
 * @returns Promise resolving to customer ID or null if not found
 */
export async function resolveCustomerId(searchParams?: URLSearchParams): Promise<number | null> {
  try {
    // Strategy 1: Check URL query param 'customerId'
    const params = searchParams || new URLSearchParams(window.location.search);
    const urlCustomerId = params.get('customerId');
    if (urlCustomerId && !isNaN(Number(urlCustomerId))) {
      const customerId = Number(urlCustomerId);
      await cacheCustomerId(customerId);
      return customerId;
    }

    // Strategy 2: Check localStorage.customerData
    const cachedCustomerId = getCachedCustomerId();
    if (cachedCustomerId) {
      return cachedCustomerId;
    }

    // Strategy 3: Derive via OnboardingService from userId
    const derivedCustomerId = await deriveCustomerIdFromUserId();
    if (derivedCustomerId) {
      await cacheCustomerId(derivedCustomerId);
      return derivedCustomerId;
    }

    return null;

  } catch (error) {
    console.warn('Failed to resolve customer ID:', error);
    return null;
  }
}

/**
 * Get cached customer ID from localStorage
 */
export function getCachedCustomerId(): number | null {
  try {
    const customerData = localStorage.getItem('customerData');
    if (!customerData) return null;

    const parsed = JSON.parse(customerData);
    const customerId = parsed?.customerId || parsed?.id;

    return customerId && !isNaN(Number(customerId)) ? Number(customerId) : null;
  } catch (error) {
    console.warn('Failed to parse cached customer data:', error);
    return null;
  }
}

/**
 * Cache customer ID in localStorage
 */
export async function cacheCustomerId(customerId: number): Promise<void> {
  try {
    const existingData = localStorage.getItem('customerData');
    const parsedData = existingData ? JSON.parse(existingData) : {};

    const updatedData = {
      ...parsedData,
      customerId,
      cachedAt: new Date().toISOString()
    };

    localStorage.setItem('customerData', JSON.stringify(updatedData));
  } catch (error) {
    console.warn('Failed to cache customer ID:', error);
  }
}

/**
 * Derive customer ID from user ID via OnboardingService
 */
export async function deriveCustomerIdFromUserId(): Promise<number | null> {
  try {
    const userData = localStorage.getItem('userData');
    if (!userData) return null;

    const parsed = JSON.parse(userData);
    const userId = parsed?.userId;

    if (!userId || isNaN(Number(userId))) return null;

    // Use OnboardingService to get customer by user ID
    const response = await ApiServices.onboarding.getCustomerByUserId(userId);

    if (response.success && response.data?.customerId) {
      return Number(response.data.customerId);
    }

    return null;

  } catch (error) {
    console.warn('Failed to derive customer ID from user ID:', error);
    return null;
  }
}

/**
 * Clear cached customer ID
 */
export function clearCachedCustomerId(): void {
  try {
    localStorage.removeItem('customerData');
  } catch (error) {
    console.warn('Failed to clear cached customer ID:', error);
  }
}

/**
 * Get customer ID synchronously (for cases where async resolution isn't needed)
 * This only checks cache and URL params, doesn't derive from user ID
 */
export function getCustomerIdSync(searchParams?: URLSearchParams): number | null {
  try {
    // Check URL query param first
    const params = searchParams || new URLSearchParams(window.location.search);
    const urlCustomerId = params.get('customerId');
    if (urlCustomerId && !isNaN(Number(urlCustomerId))) {
      return Number(urlCustomerId);
    }

    // Check cache
    return getCachedCustomerId();

  } catch (error) {
    console.warn('Failed to get customer ID synchronously:', error);
    return null;
  }
}

/**
 * Validate if a customer ID is valid
 */
export function isValidCustomerId(customerId: any): customerId is number {
  return typeof customerId === 'number' && !isNaN(customerId) && customerId > 0;
}
